// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DataSourceGridAdvanced.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import React, { Component, PropTypes } from "react";
import connect from "share/enhancers/Izenda.Connect";
import { action, observable } from "mobx";
import DataSourceGridAdvancedActions from "./Izenda.DataSourceGridAdvancedActions";
import UiState from "logic/Izenda.UiState";
import { isEmpty, find, flattenDeep, constant } from "lodash/fp";
import constants, { JOIN_TYPE_CROSS } from "logic/data/Izenda.ReportDesignerConstants";
import { Table, Column } from "share/components/controls/DataGrid/Izenda.Table";
import classnames from "classnames";
import GridLayout from "share/components/controls/Izenda.GridLayout";
import GroupPanel from "share/components/controls/Izenda.GroupPanel";
import FiltersDataSource from "modules/ReportDesigner/DataSource/Izenda.FiltersDataSource";
import DataGridSearch from "share/components/controls/DataGrid/Izenda.DataGridSearch";
import Button from "share/components/controls/Izenda.Button";
import { haveSelectedAtLeastOneQuerySource } from "./Izenda.DataTreeUtils";
import { loadDataSource } from "logic/Izenda.ReportDesignerRelationshipStore";

const TABLE_DEF = constants.TABLE_DEF.ADVANCED_MODE;

@connect({ toClass: true }, DataSourceGridAdvancedActions)
export default class DataSourceGridAdvanced extends Component {
  static propTypes = {
    store: PropTypes.object.isRequired
  };

  @observable
  gridState = {
    isDataSearchMinSize: UiState.isMobile
  };

  @action("onSearch")
  onSearch = (searchProperty: string, searchText: string): void => {
    const { store } = this.props;

    store.dataSourceDetail.pageInfo.setSearchValues(searchProperty, searchText);
    store.loadDataSourceDetail();
  };

  @action("onSearchPropertiesChange")
  onSearchPropertiesChange = (searchProperty: string, searchText: string): void => {
    const { store } = this.props;
    store.dataSourceDetail.pageInfo.setSearchValues(searchProperty, searchText);
  };

  canDrop = (target: any, source: any) => {
    const { store: { dataSourceDetail: { dataSourceGrid } }, localization } = this.props;
    const isParentCurrentRow = isEmpty(source.parentId);
    const isParentNewRow = isEmpty(target.parentId);

    if (!isParentCurrentRow) {
      const newParentRelationship = isParentNewRow ? target : find({ tempId: target.parentId }, dataSourceGrid);

      if (newParentRelationship.joinType === JOIN_TYPE_CROSS || (isParentCurrentRow && !isParentNewRow)) {
        this.warningMessage = localization.getText("REPORTDESIGNER_MESSAGE_CANNOT_DROP_KEYJOIN_HAVING_CROSS_JOIN");
        return false;
      }
    } else if (isParentCurrentRow && !isParentNewRow) {
      this.warningMessage = localization.getText("REPORTDESIGNER_MESSAGE_CANNOT_DROP_RELATIONSHIP_IN_KEYJOIN_DEST");
      return false;
    }

    return true;
  };

  @action("onDataGridSearchSizeChange")
  onDataGridSearchSizeChange = (isMinState: boolean): void => {
    this.gridState.isDataSearchMinSize = isMinState;
  };

  warning = () => {
    return (
      this.warningMessage &&
      this.props.confirmFn("Information", {
        description: this.warningMessage,
        type: "error",
        showCloseIndicator: true
      })
    );
  };

  componentWillUpdate() {
    this.resetWarningMessage();
  }

  resetWarningMessage() {
    this.warningMessage = null;
  }

  render() {
    const {
      store,
      localization,
      fieldStore,
      rebuildSearchOptions,
      onAddRelationship,
      onValidateSyntax,
      getChildItems,
      onMoveRelationship
    } = this.props;
    const {
      dataTree: { isAllDataOjectUncategory, selectedTree },
      dataSourceDetail,
      reportStore,
      isAllQuerySourceUncategorized
    } = store;

    const disableAddRelationship = !haveSelectedAtLeastOneQuerySource(selectedTree);

    const {
      displayDataSourceGrid,
      pageInfo: { searchProperty, colSortDirs },
      haveRelationships,
      totalKeyJoin,
      isLoadingRelationship
    } = dataSourceDetail;

    const flattenArraySourceGrid = flattenDeep(displayDataSourceGrid);
    const { isDataSearchMinSize } = this.gridState;
    const isVisible = isDataSearchMinSize || !UiState.isMobile;
    const widthOnMobile = isDataSearchMinSize ? 1 : 12;
    const actionBarClass = classnames({ "hidden-xs": !isVisible });

    return (
      <GridLayout className="data-source-grid">
        <GroupPanel className="izenda-FilterDatasource u_noMarginAll">
          <FiltersDataSource store={store} fieldStore={fieldStore} viewMode />
        </GroupPanel>
        <GridLayout.Row>
          <GridLayout.Col cols="7" xs={widthOnMobile}>
            <DataGridSearch
              responsive
              options={rebuildSearchOptions(this.props)}
              value={searchProperty}
              onSearch={this.onSearch}
              onSearchPropertiesChange={this.onSearchPropertiesChange}
              onSizeStateChange={this.onDataGridSearchSizeChange}
            />
          </GridLayout.Col>
          <GridLayout.Col className={actionBarClass} cols="5" xs="11">
            <div className="izenda-ActionBarRight">
              <Button
                className="add-relationship-button"
                label={localization.getText("REPORTDESIGNER_LABEL_ADD_RELATIONSHIP")}
                onClick={onAddRelationship}
                disabled={disableAddRelationship}
              />
              <Button
                label={localization.getText("REPORTDESIGNER_LABEL_FIELDS_VALIDATE_SYNTAX")}
                onClick={onValidateSyntax}
                disabled={!haveRelationships}
              />
            </div>
          </GridLayout.Col>
        </GridLayout.Row>
        <Table
          dataSource={flattenArraySourceGrid}
          onSortChange={this.onSortChange}
          getChildItems={getChildItems}
          sortInfo={colSortDirs}
          draggableTable
          move={onMoveRelationship}
          canDrag={constant(true)}
          canDrop={this.canDrop}
          warning={this.warning}
          keyJoinCount={totalKeyJoin}
          isLoading={isLoadingRelationship}
        >
          <Column {...TABLE_DEF["alias"]} cell={this.props.renderAlias} />
          <Column {...TABLE_DEF["joinType"]} cell={this.props.renderJoinType} />
          <Column {...TABLE_DEF["operator"]} cell={this.props.renderOperator} />
          {!isAllQuerySourceUncategorized && <Column {...TABLE_DEF["category"]} cell={this.props.renderCategory} />}
          <Column {...TABLE_DEF["dataObject"]} cell={this.props.renderDataObject} />
          <Column {...TABLE_DEF["joinField"]} cell={this.props.renderJoinField} />
          <Column {...TABLE_DEF["comparisonOperator"]} cell={this.props.renderComparision} />
          {!isAllQuerySourceUncategorized && <Column {...TABLE_DEF["foreignCategory"]} cell={this.props.renderCategory} />}
          <Column {...TABLE_DEF["foreignDataObject"]} cell={this.props.renderForeignDataObject} />
          <Column {...TABLE_DEF["foreignJoinfield"]} cell={this.props.renderForeignJoinField} />
          <Column {...TABLE_DEF["action"]} align="center" cell={this.props.renderAction} />
        </Table>
      </GridLayout>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/DataSource/Izenda.DataSourceGridAdvanced.js